import { Stack } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { useAlertContext } from '@/contexts/AlertContext'
import { RestartButton } from '@/features/controller/restart/components/RestartButton'
import { RestartConfirmDialog } from '@/features/controller/restart/components/RestartConfirmDialog'
import { RestartProgressBar } from '@/features/controller/restart/components/RestartProgressBar'
import { RestartStatusSummary } from '@/features/controller/restart/components/RestartStatusSummary'
import { useControllerRestartState } from '@/features/controller/restart/hooks/useControllerRestartState'
import { useControllerRestartStatusPolling } from '@/features/controller/restart/hooks/useControllerRestartStatusPolling'
import { useRestartControllerMutation } from '@/features/controller/restart/hooks/useRestartControllerMutation'
import { useRestartProgress } from '@/features/controller/restart/hooks/useRestartProgress'
import { useRestartSummary } from '@/features/controller/restart/hooks/useRestartSummary'
import { ControllerRestartMethod } from '@/features/controller/restart/types'

interface ControllerRestartDetailsProps {
  controllerId: string
}

/** Needs a ControllerRestartProvider above it, shared with anything else on the page that restarts the controller */
export function ControllerRestartDetails({ controllerId }: Readonly<ControllerRestartDetailsProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState<ControllerRestartMethod>(ControllerRestartMethod.SSH)

  const { setRestartError, restartStatus } = useControllerRestartState(controllerId)

  const smsRestartMutation = useRestartControllerMutation(controllerId, ControllerRestartMethod.SMS)
  const sshRestartMutation = useRestartControllerMutation(controllerId, ControllerRestartMethod.SSH)

  const {
    lastRestart,
    isLoading: isLoadingRestartStatus,
    error: restartStatusError,
  } = useControllerRestartStatusPolling({
    controllerId,
    enabled: true,
  })

  const isRestarting = restartStatus === 'restarting'

  // Use custom hooks to calculate progress and summary
  const { progress, statusText } = useRestartProgress(isRestarting, lastRestart)
  const lastRestartSummary = useRestartSummary(restartStatus, lastRestart)

  const handleRestartClick = () => {
    setSelectedMethod(ControllerRestartMethod.SSH)
    setOpenConfirmDialog(true)
  }

  const handleConfirmRestart = async () => {
    if (!controllerId) return

    const mutation = selectedMethod === ControllerRestartMethod.SMS ? smsRestartMutation : sshRestartMutation

    setOpenConfirmDialog(false)

    try {
      await mutation.restartController()
    } catch {
      setRestartError()
      pushAlert({
        message: t('controllers.configuration.restart_error'),
        severity: 'error',
      })
    }
  }

  const handleCancelRestart = () => {
    setOpenConfirmDialog(false)
  }

  const shouldShowRestartStatus = !isRestarting || lastRestartSummary !== null

  return (
    <>
      <Stack spacing={2}>
        <Stack alignItems="flex-start" direction="row" spacing={3}>
          <Stack spacing={1}>
            <CustomTypography color="grey.500" variant="caption">
              {t('controllers.actions.title')}
            </CustomTypography>
            <RestartButton isRestarting={isRestarting} onClick={handleRestartClick} />
          </Stack>
          {shouldShowRestartStatus && (
            <Stack flex={1} spacing={2}>
              <CustomTypography color="grey.500" variant="caption">
                {t('controllers.restart_status.last_restart_status')}
              </CustomTypography>
              <RestartStatusSummary
                error={restartStatusError}
                isLoading={isLoadingRestartStatus}
                summary={lastRestartSummary}
              />
            </Stack>
          )}
        </Stack>
        {isRestarting && <RestartProgressBar progress={progress} statusText={statusText} />}
      </Stack>

      <RestartConfirmDialog
        isPending={smsRestartMutation.isPending || sshRestartMutation.isPending}
        open={openConfirmDialog}
        selectedMethod={selectedMethod}
        onCancel={handleCancelRestart}
        onConfirm={handleConfirmRestart}
        onMethodChange={setSelectedMethod}
      />
    </>
  )
}
